import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { borderRadius, boxShadows, palette, spaces } from '@kimuno/theme';
import { Box, Icon, Paper, Stack, SvgIcon, type SxProps, Typography } from '@mui/material';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import getMuiIcon from '../../../utils/getMuiIcon';
import type { Option } from '../OnboardingOptionChipList';

interface OwnProps {
	items: Option[];
	onChange: (items: Option[]) => void;
	sxProps?: SxProps;
}

const DraggableSubjectList: React.FC<OwnProps> = (props) => {
	const { items, onChange, sxProps } = props;
	const subjectsInitial = useMemo(() => items, [items]);
	const [subjects, setSubjects] = useState(subjectsInitial);

	const handleDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination) return;

			const newItems = Array.from(subjects);
			const [movedItem] = newItems.splice(result.source.index, 1);
			newItems.splice(result.destination.index, 0, movedItem);

			setSubjects(newItems);
			if (onChange) onChange(newItems);
		},
		[subjects, onChange]
	);

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="subjects">
				{(provided) => (
					<Stack
						sx={{ ...sxProps }}
						spacing={spaces.md}
						ref={provided.innerRef}
						{...provided.droppableProps}
					>
						{subjects.map((subject, index) => (
							<Draggable key={subject.value} draggableId={subject.value} index={index}>
								{(provided, snapshot) => (
									<Paper
										ref={provided.innerRef}
										{...provided.dragHandleProps}
										{...provided.draggableProps}
										sx={{
											display: 'flex',
											alignItems: 'center',
											borderRadius: borderRadius.lg,
											border: `1px solid ${palette.elevation.outlined}`,
											padding: `${spaces.md} ${spaces.lg} ${spaces.md} ${spaces.md}`,
											justifyContent: 'space-between',
											boxShadow: snapshot.isDragging ? boxShadows.elevation3 : 'none',
											cursor: 'pointer',
										}}
									>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: spaces.md,
											}}
										>
											<Icon
												component={getMuiIcon(subject.iconName as Parameters<typeof getMuiIcon>[0])}
												sx={{
													width: 24,
													height: 24,
													fill: palette.text.primary,
												}}
											/>
											<Typography
												sx={{
													fontFamily: 'Borna',
													color: palette.text.primary,
													fontWeight: 500,
													fontStyle: 'normal',
													fontSize: 18,
													lineHeight: '28px',
													letterSpacing: '0.15px',
												}}
											>
												{subject.name}
											</Typography>
										</Box>
										<Box>
											<SvgIcon
												component={getMuiIcon('moveDragAndDrop')}
												viewBox="0 0 24 24"
												sx={{ width: 24, height: 24 }}
											/>
										</Box>
									</Paper>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</Stack>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default DraggableSubjectList;
